import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import i18n from '../i18n';

const tr = i18n.t.bind(i18n);
const eur = (c) => (Number(c || 0) / 100).toFixed(2) + '€';
const API = '/api';
const lineTotal = (qty, price, perKg) => (perKg ? Math.round(qty * price / 1000) : qty * price);

const STATUS_LABEL = {
  draft: ['⏳', 'awaiting approval', '#a67c00'],
  sent: ['👨‍🍳', 'sent', '#666'],
  preparing: ['👨‍🍳', 'preparing', '#666'],
  ready: ['✅', 'ready', '#198754'],
  served: ['🍽', 'served', '#198754'],
};

// Guest-facing page (QR on the table): browse the menu, order (staff approves),
// watch the live order, and see the bill / receipt. Customer-grade UI.
const OrderPublicPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [dead, setDead] = useState(false);
  const [view, setView] = useState('menu');    // 'menu' | 'bill'
  const [selCat, setSelCat] = useState(null);
  const [cart, setCart] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sentOk, setSentOk] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/public/ordering/${token}`, { credentials: 'omit' });
      if (!r.ok) { setDead(true); return; }
      const j = await r.json();
      setData(j);
      setSelCat((c) => c || j.categories[0]?.id || null);
    } catch { /* retry on next poll */ }
  }, [token]);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const items = useMemo(
    () => (data?.items || []).filter((i) => i.category === selCat), [data, selCat]);
  const cartTotal = cart.reduce((s, l) => s + lineTotal(l.qty, l.price_cents, l.per_kg), 0);

  const add = (mi) => {
    if (mi.out_of_stock) return;
    setCart((p) => {
      const ix = p.findIndex((l) => l.menu_item === mi.id);
      if (ix >= 0 && !mi.per_kg) { const n = [...p]; n[ix] = { ...n[ix], qty: n[ix].qty + 1 }; return n; }
      return [...p, {
        menu_item: mi.id, name: mi.name, qty: mi.per_kg ? 300 : 1,
        per_kg: mi.per_kg, price_cents: mi.price_cents, is_meat: mi.is_meat, cooktypeName: '', note: '',
      }];
    });
  };

  const send = async () => {
    if (!cart.length || busy) return;
    if (cart.some((l) => l.is_meat && !l.cooktypeName)) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/public/ordering/${token}/items`, {
        method: 'POST', credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: cart }),
      });
      if (r.ok) { setCart([]); setSentOk(true); load(); setTimeout(() => setSentOk(false), 6000); }
    } finally { setBusy(false); }
  };

  if (dead) return (
    <div style={{ fontFamily: 'system-ui, sans-serif', padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 42 }}>🍽</div>
      <h3>{tr('This order is no longer active.')}</h3>
    </div>
  );
  if (!data) return <div style={{ padding: 40, textAlign: 'center', fontFamily: 'system-ui' }}>…</div>;

  const t = data.totals || {};
  const myItems = data.myItems || [];

  return (
    <div style={{
      maxWidth: 520, margin: '0 auto', padding: '0 0 110px',
      fontFamily: 'system-ui, -apple-system, sans-serif', background: '#fafaf8', minHeight: '100vh',
    }}>
      {/* header */}
      <div style={{
        background: 'linear-gradient(135deg, #1d222b, #37404f)', color: 'white',
        padding: '22px 18px 16px', borderRadius: '0 0 22px 22px',
      }}>
        <div style={{ fontSize: 13, opacity: 0.75 }}>{tr('Welcome')} 👋</div>
        <div style={{ fontSize: 21, fontWeight: 700 }}>
          {tr('Table')} {data.tables || '—'}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          {[['menu', tr('Menu')], ['bill', `${tr('My bill')} · ${eur(t.total)}`]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)} style={{
              flex: 1, padding: '10px 6px', borderRadius: 12, border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 600,
              background: view === k ? 'white' : 'rgba(255,255,255,0.15)',
              color: view === k ? '#1d222b' : 'white',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {sentOk && (
        <div style={{
          margin: '12px 14px 0', background: '#e8f6ee', border: '1px solid #198754',
          borderRadius: 12, padding: 12, fontSize: 14,
        }}>
          ✅ {tr('Sent! Our staff will confirm your items shortly.')}
        </div>
      )}

      {view === 'menu' && (
        <div style={{ padding: '12px 14px' }}>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8 }}>
            {(data.categories || []).map((c) => (
              <button key={c.id} onClick={() => setSelCat(c.id)} style={{
                padding: '8px 14px', borderRadius: 16, whiteSpace: 'nowrap', cursor: 'pointer',
                border: '1px solid #ddd', fontSize: 13.5,
                background: selCat === c.id ? '#1d222b' : 'white',
                color: selCat === c.id ? 'white' : '#222',
              }}>{c.name}</button>
            ))}
          </div>

          {items.map((mi) => (
            <div key={mi.id} onClick={() => add(mi)} style={{
              background: 'white', border: '1px solid #ececec', borderRadius: 14,
              padding: '12px 14px', marginBottom: 8, cursor: 'pointer',
              opacity: mi.out_of_stock ? 0.45 : 1,
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
              display: 'flex', gap: 10, alignItems: 'center',
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{mi.name}</div>
                {mi.description && <div style={{ fontSize: 12, opacity: 0.6 }}>{mi.description}</div>}
                {!!mi.out_of_stock && <div style={{ fontSize: 12, color: '#b02a37' }}>{tr('Not available today')}</div>}
              </div>
              <b style={{ whiteSpace: 'nowrap', fontSize: 14 }}>{eur(mi.price_cents)}{mi.per_kg ? '/kg' : ''}</b>
              {!mi.out_of_stock && (
                <span style={{
                  width: 28, height: 28, borderRadius: 14, background: '#1d222b', color: 'white',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                }}>+</span>
              )}
            </div>
          ))}
        </div>
      )}

      {view === 'bill' && (
        <div style={{ padding: '12px 14px' }}>
          <div style={{ background: 'white', borderRadius: 14, padding: '6px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            {myItems.length === 0 && <p style={{ opacity: 0.55, fontSize: 14 }}>{tr('Nothing ordered yet.')}</p>}
            {myItems.map((i, ix) => {
              const [icon, label, color] = STATUS_LABEL[i.status] || STATUS_LABEL.sent;
              return (
                <div key={ix} style={{
                  display: 'flex', gap: 8, alignItems: 'center',
                  padding: '10px 0', borderBottom: '1px dashed #eee', fontSize: 14,
                }}>
                  <span style={{ flex: 1 }}>
                    {i.qty}{i.per_kg ? tr('g') : '×'} {i.name}
                    {i.note ? <i style={{ opacity: 0.55 }}> — {i.note}</i> : null}
                    <div style={{ fontSize: 11, color }}>{icon} {tr(label)}</div>
                  </span>
                  <b style={{ whiteSpace: 'nowrap' }}>
                    {eur(lineTotal(i.qty, i.unit_price_cents, i.per_kg))}
                  </b>
                </div>
              );
            })}

            <div style={{ padding: '12px 0 8px', fontSize: 14 }}>
              <div style={{ display: 'flex' }}><span style={{ flex: 1 }}>{tr('Subtotal')}</span>{eur(t.gross)}</div>
              {t.discount > 0 && (
                <div style={{ display: 'flex', color: '#198754' }}>
                  <span style={{ flex: 1 }}>{tr('Discounts')} 🎁</span>-{eur(t.discount)}
                </div>
              )}
              {t.paid > 0 && (
                <div style={{ display: 'flex' }}><span style={{ flex: 1 }}>{tr('Paid')}</span>{eur(t.paid)}</div>
              )}
              <div style={{ display: 'flex', fontSize: 18, fontWeight: 700, marginTop: 6, paddingTop: 8, borderTop: '2px solid #1d222b' }}>
                <span style={{ flex: 1 }}>{tr('Total')}</span>{eur(t.total)}
              </div>
            </div>
          </div>

          {data.receipt && (
            <div style={{
              marginTop: 12, background: 'white', borderRadius: 14, padding: 14,
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)', fontSize: 13, textAlign: 'center',
            }}>
              {data.receipt.status === 'issued' ? (
                <>
                  <b>🧾 {tr('Your receipt has been issued.')}</b>
                  {data.receipt.qr_url && (
                    <div style={{ marginTop: 6 }}>
                      <a href={data.receipt.qr_url} target="_blank" rel="noreferrer">{tr('View receipt')}</a>
                    </div>
                  )}
                </>
              ) : (
                <span style={{ opacity: 0.7 }}>🧾 {tr('Receipt is being issued…')}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* sticky cart */}
      {view === 'menu' && cart.length > 0 && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, background: 'white',
          borderTop: '1px solid #e5e5e5', boxShadow: '0 -6px 20px rgba(0,0,0,0.1)',
          padding: '10px 14px calc(10px + env(safe-area-inset-bottom, 0px))',
        }}>
          <div style={{ maxWidth: 520, margin: '0 auto' }}>
            {cart.map((l, ix) => (
              <div key={ix} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, fontSize: 13.5 }}>
                <span style={{ flex: 1 }}>{l.name}</span>
                {l.per_kg ? (
                  <><input type="number" step={50} min={100} value={l.qty} inputMode="numeric"
                    onChange={(e) => setCart((p) => p.map((x, i) => i === ix ? { ...x, qty: Number(e.target.value) || 0 } : x))}
                    style={{ width: 62, padding: 6, borderRadius: 8, border: '1px solid #ddd' }} /><small>{tr('g')}</small></>
                ) : (
                  <>
                    <button onClick={() => setCart((p) => p.map((x, i) => i === ix ? { ...x, qty: Math.max(1, x.qty - 1) } : x))}
                      style={{ width: 28, height: 28, borderRadius: 14, border: '1px solid #ddd', background: 'white' }}>−</button>
                    <b>{l.qty}</b>
                    <button onClick={() => setCart((p) => p.map((x, i) => i === ix ? { ...x, qty: x.qty + 1 } : x))}
                      style={{ width: 28, height: 28, borderRadius: 14, border: '1px solid #ddd', background: 'white' }}>+</button>
                  </>
                )}
                {!!l.is_meat && (
                  <select value={l.cooktypeName}
                    style={{ maxWidth: 96, padding: 6, borderRadius: 8, borderColor: l.cooktypeName ? '#ddd' : '#b02a37' }}
                    onChange={(e) => setCart((p) => p.map((x, i) => i === ix ? { ...x, cooktypeName: e.target.value } : x))}>
                    <option value="">{tr('Cook type')}…</option>
                    {(data.cooktypes || []).map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                )}
                <button onClick={() => setCart((p) => p.filter((_, i) => i !== ix))}
                  style={{ border: 'none', background: 'none', color: '#b02a37', fontSize: 15 }}>✕</button>
              </div>
            ))}
            <button onClick={send} disabled={busy || cart.some((l) => l.is_meat && !l.cooktypeName)}
              style={{
                width: '100%', padding: 13, borderRadius: 12, border: 'none', cursor: 'pointer',
                background: '#198754', color: 'white', fontWeight: 700, fontSize: 15,
              }}>
              {busy ? '…' : `${tr('Send to staff')} · ${eur(cartTotal)}`}
            </button>
          </div>
        </div>
      )}
      <div style={{ textAlign: 'center', padding: '14px 0 18px', fontSize: 11.5, color: '#9ca3af' }}>
        Powered by EstiaSync · <a href="/api/public/legal/privacy" target="_blank" rel="noreferrer" style={{ color: '#9ca3af' }}>{tr('Privacy')}</a>
      </div>
    </div>
  );
};

export default OrderPublicPage;
